/**
 * AdaptiveGuideContext — B-2026-0056, B-2026-0085, B-2026-0086
 *
 * Central state bus for the Adaptive Guidance Overlay. Components call
 * `useAdaptiveGuide().report({ ... })` as a side-effect to push their
 * current UI state into the context. The overlay reads this state and
 * renders contextually relevant content.
 *
 * Design notes:
 * - The provider owns NO business state; it is a thin "mirror" that reflects
 *   state from surrounding components via the `report()` callback.
 * - Prevents deep prop-drilling: any component in the subtree can push updates
 *   without threading props through intermediate layers.
 * - `useAdaptiveGuide()` throws a descriptive error when used outside a
 *   provider, making integration mistakes obvious during development.
 *
 * B-2026-0085: adds `isDrawerCollapsed` + `setDrawerCollapsed` for persisted
 * retract/expand state of the desktop drawer.
 *
 * B-2026-0086: adds `activeSection` mirrored from the useSectionSpy producer.
 */

import { createContext, useContext } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ActiveSurface = 'studio' | 'evaluation-results' | 'template-details' | null;

export interface AdaptiveGuideState {
  /** Which surface the overlay is mounted on. */
  activeSurface: ActiveSurface;

  /** Studio: which annotation tool type is currently active (e.g. "axis", "region"). */
  selectedTool: string | null;

  /** Studio: the ID of the annotation the coach is currently editing. */
  selectedAnnotationId: string | null;

  /** Studio: which inspector section/panel is currently expanded. */
  activePanel: string | null;

  /**
   * The `data-field-id` of the input/control that currently has focus.
   * Matched against the FIELD_IDS constant. The overlay can use this to
   * provide field-level contextual hints.
   */
  focusedField: string | null;

  /** Evaluation results: index of the phase tab the athlete has selected (0-based). */
  activePhase: number | null;

  /**
   * Evaluation results: the region or goal key the athlete has highlighted
   * (e.g. "upper_body" or "axis_goal_0").
   */
  selectedRegion: string | null;

  /**
   * Evaluation results (B-2026-0086): the `data-guide-section` key of the
   * section currently most visible in the viewport. Produced by the
   * useSectionSpy hook mounted in EvaluationResults.
   */
  activeSection: string | null;

  /** Whether the guide panel is currently open/visible. */
  isGuideOpen: boolean;
}

export interface AdaptiveGuideContextValue extends AdaptiveGuideState {
  /**
   * Merge-update the context state. Only the supplied keys are changed;
   * omitted keys retain their current values.
   *
   * Call this as a side-effect (inside a useEffect or event handler) to
   * keep the context in sync with local component state:
   *
   * ```tsx
   * const { report } = useAdaptiveGuide();
   * useEffect(() => { report({ selectedTool: activeTool }); }, [activeTool]);
   * ```
   */
  report: (partial: Partial<AdaptiveGuideState>) => void;

  /** Convenience shorthand: toggle the guide open/closed. */
  setGuideOpen: (open: boolean) => void;

  /**
   * B-2026-0085: Whether the desktop drawer is retracted to its handle.
   * Persisted to localStorage so the user's preference survives navigation.
   */
  isDrawerCollapsed: boolean;

  /** Toggle or set the drawer's retracted/expanded state. */
  setDrawerCollapsed: (collapsed: boolean) => void;
}

// ---------------------------------------------------------------------------
// Context + default value
// ---------------------------------------------------------------------------

// Exported so AdaptiveGuideProvider.tsx (react-refresh/only-export-components
// split — see that file's comment) can construct the Provider.
export const DRAWER_COLLAPSED_STORAGE_KEY = 'guide-drawer-collapsed';

export function readDrawerCollapsedFromStorage(): boolean {
  try {
    return localStorage.getItem(DRAWER_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export const INITIAL_STATE: AdaptiveGuideState = {
  activeSurface: null,
  selectedTool: null,
  selectedAnnotationId: null,
  activePanel: null,
  focusedField: null,
  activePhase: null,
  selectedRegion: null,
  activeSection: null,
  isGuideOpen: false,
};

export const AdaptiveGuideContext = createContext<AdaptiveGuideContextValue | null>(null);
AdaptiveGuideContext.displayName = 'AdaptiveGuideContext';

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the full `AdaptiveGuideContextValue`.
 *
 * Must be used inside an `<AdaptiveGuideProvider>`. Throws a descriptive
 * error if the provider is missing so integration mistakes are caught
 * at development time.
 */
export function useAdaptiveGuide(): AdaptiveGuideContextValue {
  const ctx = useContext(AdaptiveGuideContext);
  if (ctx === null) {
    throw new Error(
      '[useAdaptiveGuide] This hook must be used inside an <AdaptiveGuideProvider>. ' +
      'Wrap the relevant route element (e.g. <EvaluationResults>, <CreateTemplate>, ' +
      'or <TemplateDetails>) with <AdaptiveGuideProvider surface="evaluation-results">, ' +
      '<AdaptiveGuideProvider surface="studio">, or ' +
      '<AdaptiveGuideProvider surface="template-details">.'
    );
  }
  return ctx;
}
